import React, { useState, useEffect } from 'react';
import { Clock, Plus, X, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { api } from '../lib/api';
import { toast } from 'sonner';

const WEEKDAYS = [
  { id: 'mon', label: 'Lun' },
  { id: 'tue', label: 'Mar' },
  { id: 'wed', label: 'Mié' },
  { id: 'thu', label: 'Jue' },
  { id: 'fri', label: 'Vie' },
  { id: 'sat', label: 'Sáb' },
  { id: 'sun', label: 'Dom' },
];

const ScheduleEditor = ({ open, onOpenChange, station, onSaved }) => {
  const [schedule, setSchedule] = useState({ mon: [], tue: [], wed: [], thu: [], fri: [], sat: [], sun: [] });
  const [tracking, setTracking] = useState(true);
  const [assignedIds, setAssignedIds] = useState([]);
  const [capturers, setCapturers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [newSlot, setNewSlot] = useState('');
  const [activeDay, setActiveDay] = useState('mon');

  useEffect(() => {
    if (!station || !open) return;
    setSchedule(station.schedule || { mon: [], tue: [], wed: [], thu: [], fri: [], sat: [], sun: [] });
    setTracking(station.tracking_enabled !== false);
    setAssignedIds(station.assigned_capturer_ids || []);
    api.get('/users').then(r => setCapturers(r.data.filter(u => u.role === 'capturer'))).catch(() => {});
  }, [station, open]);

  const addSlot = () => {
    if (!/^\d{1,2}:\d{2}$/.test(newSlot)) { toast.error('Formato HH:MM'); return; }
    const [h, m] = newSlot.split(':').map(Number);
    if (h > 23 || m > 59) { toast.error('Hora inválida'); return; }
    const formatted = `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}`;
    setSchedule(s => ({ ...s, [activeDay]: Array.from(new Set([...(s[activeDay] || []), formatted])).sort() }));
    setNewSlot('');
  };

  const removeSlot = (day, slot) => {
    setSchedule(s => ({ ...s, [day]: s[day].filter(x => x !== slot) }));
  };

  const copyToAll = () => {
    const slots = schedule[activeDay] || [];
    setSchedule(WEEKDAYS.reduce((acc, d) => ({ ...acc, [d.id]: [...slots] }), {}));
    toast.success('Copiado a todos los días');
  };

  const copyToWeekdays = () => {
    const slots = schedule[activeDay] || [];
    setSchedule(s => ({
      ...s,
      mon: [...slots], tue: [...slots], wed: [...slots], thu: [...slots], fri: [...slots],
    }));
    toast.success('Copiado a Lun-Vie');
  };

  const toggleAssigned = (uid) => {
    setAssignedIds(ids => ids.includes(uid) ? ids.filter(x => x !== uid) : [...ids, uid]);
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.patch(`/stations/${station.id}/schedule`, {
        schedule, tracking_enabled: tracking, assigned_capturer_ids: assignedIds,
      });
      const warnings = r?.data?._warnings || [];
      if (warnings.length > 0) {
        toast.warning(`Horario guardado · ${warnings.length} día(s) con menos de 3 capturas`, { duration: 6000 });
      } else {
        toast.success('Horario guardado');
      }
      onSaved && onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  if (!station) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-[#f8f6f1] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Horario · {station.name}</DialogTitle>
          <p className="text-[12px] text-[#8a8579]">Define las horas de captura por día. Hora Guatemala (GT-6).</p>
        </DialogHeader>

        <div className="flex items-center justify-between bg-white border border-[#e8e5de] rounded-lg p-3 mb-4">
          <div>
            <p className="text-[13px] font-medium">Tracking activo</p>
            <p className="text-[11px] text-[#8a8579]">Si está apagado, no se enviarán alertas por capturas faltantes.</p>
          </div>
          <Switch checked={tracking} onCheckedChange={setTracking}/>
        </div>

        {/* Day selector */}
        <div className="flex gap-1 mb-3 bg-white border border-[#e8e5de] rounded-full p-1">
          {WEEKDAYS.map(d => (
            <button key={d.id} onClick={() => setActiveDay(d.id)}
              className={`flex-1 h-8 text-[12px] rounded-full transition relative ${activeDay===d.id?'bg-[#1c1c1c] text-[#f8f6f1]':'text-[#5a5a5a] hover:bg-[#f3f0e9]'}`}>
              {d.label}
              {(schedule[d.id]?.length || 0) > 0 && (
                <span className={`absolute -top-1 -right-1 w-4 h-4 rounded-full text-[9px] flex items-center justify-center ${activeDay===d.id?'bg-[#f8f6f1] text-[#1c1c1c]':'bg-[#1c1c1c] text-[#f8f6f1]'}`}>{schedule[d.id].length}</span>
              )}
            </button>
          ))}
        </div>

        {/* Slots for active day */}
        <div className="bg-white border border-[#e8e5de] rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <Label className="text-[12px] uppercase tracking-wider text-[#8a8579]">Horas programadas</Label>
            <div className="flex gap-1">
              <button onClick={copyToWeekdays} className="text-[10.5px] text-[#1c1c1c] underline underline-offset-2">Copiar a L-V</button>
              <span className="text-[#8a8579]">·</span>
              <button onClick={copyToAll} className="text-[10.5px] text-[#1c1c1c] underline underline-offset-2">a todos los días</button>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mb-3 min-h-[36px]">
            {(schedule[activeDay] || []).length === 0 ? (
              <p className="text-[12px] text-[#8a8579] py-2">Sin horarios. Agrega abajo.</p>
            ) : schedule[activeDay].map(slot => (
              <span key={slot} className="inline-flex items-center gap-1.5 px-3 h-8 bg-[#f3f0e9] rounded-full text-[12.5px]">
                <Clock size={11}/> {slot}
                <button onClick={() => removeSlot(activeDay, slot)} className="text-[#8a8579] hover:text-[#b91c1c] ml-0.5"><X size={11}/></button>
              </span>
            ))}
          </div>

          <div className="flex gap-2">
            <Input type="time" value={newSlot} onChange={e => setNewSlot(e.target.value)} placeholder="HH:MM" className="w-32 h-9 bg-white text-[13px]"/>
            <Button size="sm" onClick={addSlot} className="h-9 bg-[#1c1c1c] hover:bg-black text-[#f8f6f1]"><Plus size={14}/> Agregar</Button>
          </div>
        </div>

        {/* Capturers assignment */}
        <div className="bg-white border border-[#e8e5de] rounded-xl p-4 mt-4">
          <Label className="text-[12px] uppercase tracking-wider text-[#8a8579] mb-3 block">Capturadores asignados</Label>
          {capturers.length === 0 ? (
            <p className="text-[12px] text-[#8a8579]">Sin capturadores activos. Crea usuarios primero.</p>
          ) : (
            <div className="space-y-1.5 max-h-48 overflow-y-auto">
              {capturers.map(c => (
                <label key={c.id} className="flex items-center gap-2.5 px-2 py-1.5 rounded hover:bg-[#f3f0e9] cursor-pointer">
                  <input type="checkbox" checked={assignedIds.includes(c.id)} onChange={() => toggleAssigned(c.id)} className="w-4 h-4 rounded border-[#d4cfc4]"/>
                  <span className="text-[12.5px] flex-1">{c.name}</span>
                  <span className="text-[10.5px] text-[#8a8579]">{c.region}</span>
                </label>
              ))}
            </div>
          )}
          <p className="text-[11px] text-[#8a8579] mt-2">Si se pasa más de 1 hora sin captura, se notifica al capturador. Tras 2 h, también al territory manager de la región.</p>
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={save} disabled={saving} className="bg-[#1c1c1c] text-[#f8f6f1] hover:bg-black">
            {saving ? <><Loader2 size={14} className="animate-spin mr-2"/>Guardando…</> : 'Guardar horario'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ScheduleEditor;
